import { Component, signal, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { NotificationService } from '../../services/notification.service';
import {
  PaymentService,
  PaymentRequest,
  PaymentResponse,
  ApiPlan,
} from '../../services/payment.service';
import { AuthService } from '../../services/auth.service';
import { SocketService } from '../../services/socket.service';
import { ChatService } from '../../services/chat.service';
import { Subscription, firstValueFrom, interval, of } from 'rxjs';
import { startWith, switchMap, takeWhile, tap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

interface Plan {
  id: number;
  name: string;
  price: number;
  duration: 'monthly' | 'annual' | 'custom';
  durationDays: number;
  description: string;
  features: string[];
  popular?: boolean;
}

/** Planes que no se muestran en el registro (p. ej. gratuito u otros no vendibles aquí). */
const REGISTER_HIDDEN_PLAN_IDS = new Set([4, 7, 8]);

function normalizePagoEstado(estado: string | undefined | null): string {
  return (estado ?? '').toString().trim().toLowerCase();
}

@Component({
  selector: 'app-register',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './register.html',
  styleUrl: './register.css',
})
export class Register implements OnInit, OnDestroy {
  @ViewChild('paymentIframe') paymentIframe!: ElementRef<HTMLIFrameElement>;

  // Form groups
  userInfoForm: FormGroup;
  paymentForm: FormGroup;

  // State signals
  currentStep = signal<1 | 2>(1);
  selectedPlan = signal<Plan | null>(null);
  isLoading = signal(false);
  isLoadingPlans = signal(true);
  errorMessage = signal('');
  paymentStatus = signal<'idle' | 'processing' | 'success' | 'failed'>('idle');
  paymentId = signal<number | null>(null);
  iframeUrl = signal<string | null>(null);
  showPassword = signal(false);
  hasReferralCode = signal(false); // Indica si vino con código de referido

  /** Tarjeta (APay) o criptomonedas (NOWPayments) */
  paymentMethod = signal<'card' | 'crypto'>('card');
  payCurrency = signal('btc');
  cryptoCheckoutCreated = signal(false);
  cryptoPayAddress = signal('');
  cryptoPayAmount = signal('');
  cryptoPayCurrencyCode = signal('');
  cryptoPriceAmount = signal<number | null>(null);
  cryptoPriceCurrency = signal('');
  cryptoEstado = signal('');
  cryptoPuedeRegistrarse = signal(false);
  cryptoEmailUsed = signal('');

  readonly cryptoCurrencyOptions = [
    { value: 'btc', label: 'Bitcoin (BTC)' },
    { value: 'eth', label: 'Ethereum (ETH)' },
    { value: 'usdttrc20', label: 'USDT (TRC20)' },
    { value: 'usdterc20', label: 'USDT (ERC20)' },
    { value: 'ltc', label: 'Litecoin (LTC)' }
  ];

  // Subscription for polling
  private pollingSubscription?: Subscription;
  private cryptoPollingSubscription?: Subscription;
  private nowPayId: number | null = null;

  // Available plans (loaded from API)
  plans: Plan[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private paymentService: PaymentService,
    private authService: AuthService,
    private socketService: SocketService,
    private chatService: ChatService
  ) {
    // User information form
    this.userInfoForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.pattern(/^[0-9]{8,15}$/)]],
      contrasena: ['', [Validators.required, Validators.minLength(6)]],
      referralCode: [''], // Optional
      terms: [false, [Validators.requiredTrue]]
    });

    // Payment form with custom validators
    this.paymentForm = this.fb.group({
      cardNumber: ['', [Validators.required, this.cardNumberValidator]],
      cardholderName: ['', [Validators.required, Validators.minLength(3)]],
      expiryDate: ['', [Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])\/[0-9]{2}$/)]],
      cvv: ['', [Validators.required, Validators.pattern(/^[0-9]{3,4}$/)]]
    });
  }

  // Custom validator for card number (accepts with or without spaces)
  private cardNumberValidator(control: any) {
    if (!control.value) {
      return null;
    }
    const cleanNumber = control.value.replace(/\s/g, '');
    const isValid = /^[0-9]{13,19}$/.test(cleanNumber);
    return isValid ? null : { invalidCardNumber: true };
  }

  ngOnInit(): void {
    window.scrollTo(0, 0);
    
    // Capturar código de referido de la URL si existe
    this.route.queryParams.subscribe(params => {
      const codigoReferido = params['ref'];
      if (codigoReferido) {
        console.log('🔗 Código de referido detectado en URL:', codigoReferido);
        this.hasReferralCode.set(true);
        this.userInfoForm.patchValue({
          referralCode: codigoReferido
        });
        // Deshabilitar el campo para que no se pueda modificar
        this.userInfoForm.get('referralCode')?.disable();
        
        // Mostrar notificación al usuario
        this.notificationService.info(
          'Código de Referido',
          `Has sido invitado con el código: ${codigoReferido}`
        );
      }
    });
    
    this.loadPlans();
  }

  /**
   * Carga los planes desde la API
   */
  private loadPlans(): void {
    this.isLoadingPlans.set(true);

    this.paymentService.getPlanes().subscribe({
      next: (response) => {
        if (response.ok && response.data) {
          // Filtrar solo planes activos y transformar a formato interno
          this.plans = response.data
            .filter(
              plan =>
                plan.activo &&
                parseFloat(plan.precio) > 0 &&
                !REGISTER_HIDDEN_PLAN_IDS.has(plan.id_plan)
            )
            .map(apiPlan => this.transformApiPlan(apiPlan));

          // Marcar el plan más popular (el de mayor duración o el que tenga más cursos)
          if (this.plans.length > 0) {
            const mostPopular = this.plans.reduce((prev, current) =>
              current.durationDays > prev.durationDays ? current : prev
            );
            mostPopular.popular = true;
          }
        }
        this.isLoadingPlans.set(false);
      },
      error: (error) => {
        console.error('Error cargando planes:', error);
        this.notificationService.error('Error', 'No se pudieron cargar los planes disponibles');
        this.isLoadingPlans.set(false);
      }
    });
  }

  /**
   * Transforma un plan de la API al formato interno
   */
  private transformApiPlan(apiPlan: ApiPlan): Plan {
    const price = parseFloat(apiPlan.precio);
    const durationDays = apiPlan.duracion_dias;

    // Determinar el tipo de duración
    let duration: 'monthly' | 'annual' | 'custom' = 'custom';
    if (durationDays === 30 || durationDays === 31) {
      duration = 'monthly';
    } else if (durationDays === 365 || durationDays === 366) {
      duration = 'annual';
    }

    // Crear lista de características desde la descripción HTML
    const features: string[] = [];

    // Si la descripción contiene HTML, extraer el contenido de las listas
    if (apiPlan.descripcion && apiPlan.descripcion.includes('<')) {
      // Usar DOMParser para extraer contenido de listas
      const parser = new DOMParser();
      const doc = parser.parseFromString(apiPlan.descripcion, 'text/html');

      // Extraer items de listas <li>
      const listItems = doc.querySelectorAll('li');
      listItems.forEach(item => {
        const text = item.textContent?.trim();
        if (text) {
          features.push(text);
        }
      });

      // Si no hay items de lista, extraer párrafos
      if (features.length === 0) {
        const paragraphs = doc.querySelectorAll('p');
        paragraphs.forEach(p => {
          const text = p.textContent?.trim();
          if (text && !text.includes('¿QUÉ RECIBIRÁS')) {
            features.push(text);
          }
        });
      }
    } else if (apiPlan.descripcion) {
      // Si es texto plano, agregarlo como característica
      features.push(apiPlan.descripcion);
    }

    // Agregar información sobre cursos incluidos
    if (apiPlan.cursos && apiPlan.cursos.length > 0) {
      features.push(`Incluye ${apiPlan.cursos.length} curso${apiPlan.cursos.length > 1 ? 's' : ''}`);
    }

    // Agregar duración como característica si no está ya en la descripción
    const durationText = duration === 'monthly' ? '30 días de acceso' :
      duration === 'annual' ? '1 año completo de acceso' :
        `${durationDays} días de acceso`;

    if (!features.some(f => f.toLowerCase().includes('año') || f.toLowerCase().includes('mes') || f.toLowerCase().includes('día'))) {
      features.push(durationText);
    }

    return {
      id: apiPlan.id_plan,
      name: apiPlan.nombre_plan,
      price: price,
      duration: duration,
      durationDays: durationDays,
      description: apiPlan.descripcion, // Mantener HTML original
      features: features,
      popular: false // Se asignará después
    };
  }

  ngOnDestroy(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
    this.stopCryptoPolling();
  }

  /** Plan de pago vendible aquí: puede mostrar opción cripto. */
  canOfferCrypto(): boolean {
    const p = this.selectedPlan();
    return !!p && !REGISTER_HIDDEN_PLAN_IDS.has(p.id) && p.price > 0;
  }

  selectPaymentMethod(method: 'card' | 'crypto'): void {
    this.paymentMethod.set(method);
    this.errorMessage.set('');
    if (method === 'card') {
      this.resetCryptoCheckout();
    }
  }

  onPayCurrencyChange(event: Event): void {
    const v = (event.target as HTMLSelectElement).value;
    this.payCurrency.set(v);
  }

  private resetCryptoCheckout(): void {
    this.stopCryptoPolling();
    this.cryptoCheckoutCreated.set(false);
    this.cryptoPayAddress.set('');
    this.cryptoPayAmount.set('');
    this.cryptoPayCurrencyCode.set('');
    this.cryptoPriceAmount.set(null);
    this.cryptoPriceCurrency.set('');
    this.cryptoEstado.set('');
    this.cryptoPuedeRegistrarse.set(false);
    this.cryptoEmailUsed.set('');
    this.nowPayId = null;
  }

  private stopCryptoPolling(): void {
    if (this.cryptoPollingSubscription) {
      this.cryptoPollingSubscription.unsubscribe();
      this.cryptoPollingSubscription = undefined;
    }
  }

  /** URI para wallet (QR). */
  cryptoPaymentUri(): string {
    const addr = this.cryptoPayAddress();
    const amt = this.cryptoPayAmount();
    const cur = (this.cryptoPayCurrencyCode() || this.payCurrency()).toLowerCase();
    if (!addr) return '';
    if (cur === 'btc' || cur === 'bitcoin') {
      return `bitcoin:${addr}?amount=${encodeURIComponent(amt)}`;
    }
    if (cur === 'eth' || cur === 'ethereum') {
      return `ethereum:${addr}`;
    }
    return addr;
  }

  cryptoQrImageUrl(): string {
    const data = this.cryptoPaymentUri();
    if (!data) return '';
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(data)}`;
  }

  getCryptoStatusMessage(): string {
    const e = this.cryptoEstado();
    if (e === 'Pendiente') {
      return 'Esperando confirmación en la blockchain… Envía exactamente el monto indicado.';
    }
    if (e === 'Esperando_Confirmacion') {
      return 'Pago recibido. Validación del equipo en curso. Revisa tu correo (contraseña temporal). No intentes iniciar sesión hasta que el pago quede confirmado.';
    }
    if (e === 'Exitoso' && this.cryptoPuedeRegistrarse()) {
      return 'Listo. Ya puedes iniciar sesión con el correo y la contraseña que te enviamos.';
    }
    if (e === 'Exitoso' && !this.cryptoPuedeRegistrarse()) {
      return 'Pago confirmado en sistema; esperando activación final.';
    }
    if (e === 'Fallido') {
      return 'El pago falló, expiró o fue reembolsado. Puedes generar uno nuevo.';
    }
    return 'Consultando estado del pago…';
  }

  async copyToClipboard(text: string, label: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.notificationService.success('Copiado', `${label} copiado al portapapeles`);
    } catch {
      this.notificationService.warning('No se pudo copiar', 'Copia el texto manualmente');
    }
  }

  async createCryptoPayment(): Promise<void> {
    const plan = this.selectedPlan();
    if (!plan || REGISTER_HIDDEN_PLAN_IDS.has(plan.id)) {
      this.notificationService.warning('Plan no válido', 'Este plan no admite pago con criptomonedas');
      return;
    }
    if (!this.userInfoForm.valid) {
      this.errorMessage.set('Completa el paso anterior correctamente');
      return;
    }

    const email = (this.userInfoForm.get('email')?.value as string)?.trim();
    if (!email) return;

    this.isLoading.set(true);
    this.errorMessage.set('');

    try {
      const res = await firstValueFrom(
        this.paymentService.createNowPaymentsPayment({
          email,
          id_plan: plan.id,
          pay_currency: this.payCurrency(),
          order_description: `${environment.appName} — ${plan.name}`
        })
      );

      if (!res.ok) {
        throw new Error(res.msg || 'No se pudo crear el pago');
      }

      this.nowPayId = res.id_pago;
      this.cryptoEmailUsed.set(email);
      this.cryptoPayAddress.set(res.pay_address || '');
      this.cryptoPayAmount.set(String(res.pay_amount ?? ''));
      this.cryptoPayCurrencyCode.set((res.pay_currency || this.payCurrency()).toLowerCase());
      this.cryptoPriceAmount.set(res.price_amount ?? null);
      this.cryptoPriceCurrency.set(res.price_currency || 'usd');
      this.cryptoCheckoutCreated.set(true);
      this.cryptoEstado.set('Pendiente');

      this.notificationService.info(
        'Pago creado',
        'Envía exactamente el monto indicado a la dirección mostrada.'
      );

      this.startCryptoPolling();
    } catch (err: any) {
      const msg =
        err?.error?.msg ||
        err?.message ||
        'Error al crear el pago con criptomonedas. Intenta de nuevo o usa tarjeta.';
      this.errorMessage.set(msg);
      this.notificationService.error('Error', msg);
    } finally {
      this.isLoading.set(false);
    }
  }

  private startCryptoPolling(): void {
    this.stopCryptoPolling();
    const id = this.nowPayId;
    const email = this.cryptoEmailUsed();
    if (id == null || !email) return;

    this.cryptoPollingSubscription = interval(20000)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.paymentService.getNowPaymentsPagoStatus(id, email).pipe(
            catchError((err) => {
              console.error('Error polling NOWPayments:', err);
              const msg = err?.error?.msg || 'Error al consultar el estado del pago';
              if (err?.status === 403) {
                this.errorMessage.set(msg);
                this.stopCryptoPolling();
              }
              return of(null);
            })
          )
        ),
        tap((response) => {
          if (!response?.ok) return;
          this.cryptoEstado.set(response.estado);
          this.cryptoPuedeRegistrarse.set(!!response.puede_registrarse);

          const est = normalizePagoEstado(response.estado);

          if (est === 'exitoso' && response.puede_registrarse) {
            this.stopCryptoPolling();
            void this.finalizeCryptoPaymentSuccess();
          } else if (est === 'fallido') {
            this.stopCryptoPolling();
            this.isLoading.set(false);
            this.notificationService.error('Pago no completado', 'Puedes generar un nuevo pago o elegir tarjeta.');
          }
        }),
        takeWhile(
          (response) => {
            if (!response?.ok) return true;
            const est = normalizePagoEstado(response.estado);
            if (est === 'exitoso' && response.puede_registrarse) return false;
            if (est === 'fallido') return false;
            return true;
          },
          true
        )
      )
      .subscribe();
  }

  /**
   * Tras pago cripto confirmado: mismo POST /auth/new que con tarjeta (paymentId + datos del formulario).
   * Si el backend ya creó la cuenta por IPN, suele responder error amigable → redirigimos a login.
   */
  private async finalizeCryptoPaymentSuccess(): Promise<void> {
    const idPago = this.nowPayId;
    if (idPago == null) return;

    this.paymentStatus.set('processing');
    this.isLoading.set(true);
    this.errorMessage.set('');

    try {
      await this.registerAfterSuccessfulPayment(idPago, '');
    } catch (error: any) {
      console.error('Error en registro post-pago cripto:', error);
      this.isLoading.set(false);
      this.paymentStatus.set('success');
      const ref = `id de pago: ${idPago}`;
      this.errorMessage.set(
        'Tu pago fue exitoso, pero hubo un error creando tu cuenta. Contacta soporte con ' + ref
      );
      this.notificationService.warning('Atención', 'Pago exitoso pero error al completar el registro. Contacta soporte.');
    }
  }

  retryCryptoPayment(): void {
    this.resetCryptoCheckout();
    this.paymentStatus.set('idle');
    this.errorMessage.set('');
  }

  // Plan selection
  selectPlan(plan: Plan) {
    this.selectedPlan.set(plan);
    if (REGISTER_HIDDEN_PLAN_IDS.has(plan.id)) {
      this.paymentMethod.set('card');
      this.resetCryptoCheckout();
    }
  }

  // Step navigation
  async goToStep2() {
    if (this.userInfoForm.valid && this.selectedPlan()) {
      
      // Verificar disponibilidad del email
      const email = this.userInfoForm.get('email')?.value;
      this.isLoading.set(true); // Reutilizamos isLoading para mostrar que estamos verificando
      
      try {
        const checkResponse = await firstValueFrom(this.authService.checkEmail(email));
        
        if (checkResponse.ok) {
           // Email disponible
           this.isLoading.set(false);
           this.currentStep.set(2);
           this.errorMessage.set('');
           window.scrollTo(0, 0);
        } else {
           // Email no disponible
           this.isLoading.set(false);
           this.errorMessage.set(checkResponse.msg || 'El correo electrónico ya está registrado');
           this.notificationService.warning('Atención', 'El correo electrónico ya está registrado');
        }
      } catch (error) {
        this.isLoading.set(false);
        this.errorMessage.set('Error al verificar el correo electrónico');
        console.error('Error verifying email:', error);
      }

    } else {
      if (!this.selectedPlan()) {
        this.errorMessage.set('Por favor, selecciona un plan');
      } else {
        this.errorMessage.set('Por favor, completa todos los campos correctamente');
        Object.keys(this.userInfoForm.controls).forEach(key => {
          this.userInfoForm.get(key)?.markAsTouched();
        });
      }
    }
  }

  goBackToStep1() {
    this.currentStep.set(1);
    this.errorMessage.set('');
    this.paymentStatus.set('idle');
    this.paymentMethod.set('card');
    this.resetCryptoCheckout();
    window.scrollTo(0, 0);
  }

  navigateToLogin() {
    this.router.navigate(['/login']);
  }

  // Payment processing
  async processPayment() {
    if (!this.paymentForm.valid || !this.selectedPlan()) {
      this.errorMessage.set('Por favor, completa todos los campos de pago correctamente');
      Object.keys(this.paymentForm.controls).forEach(key => {
        this.paymentForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.isLoading.set(true);
    this.paymentStatus.set('processing');
    this.errorMessage.set('');

    const plan = this.selectedPlan()!;
    const paymentData = this.paymentForm.value;

    // Detectar tipo de tarjeta
    const cardType = this.paymentService.detectCardType(paymentData.cardNumber);

    try {
      // 1. Procesar transacción con APay
      const expiryParts = paymentData.expiryDate.split('/');
      const expDate = `${expiryParts[1]}${expiryParts[0]}`; // YYMM format

      const apayTransaction = {
        monto: plan.price,
        card_number: paymentData.cardNumber.replace(/\s/g, ''),
        cvv: paymentData.cvv,
        exp_date: expDate,
        cardholderName: paymentData.cardholderName,
        token: this.paymentService.getApayToken(),
        ambiente: environment.ambiente, // 0 = test, 1 = production
        metodo: 0 // 0 = PUT, 1 = POST
      };

      const apayResponse = await firstValueFrom(this.paymentService.processApayTransaction(apayTransaction));

      if (!apayResponse) {
        throw new Error('No se pudo procesar la transacción con APay');
      }

      // Manejar errores de validación de APay
      if (apayResponse.msg && !apayResponse.url) {
        this.handleApayError(apayResponse.msg);
        return;
      }

      // 2. Crear el pago en nuestro sistema con la referencia de APay
      const paymentRequest: PaymentRequest = {
        id_plan: plan.id,
        referencia_transaccion: apayResponse.id, // ID devuelto por APay
        monto: plan.price,
        metodo_pago: cardType,
        id_link: apayResponse.idLink || null,
        digits_card: paymentData.cardNumber.slice(-4) // Últimos 4 dígitos
      };

      // Nota: id_usuario no se envía o se envía como 0 si el backend lo requiere, 
      // pero el usuario no existe aún.

      const paymentResponse = await firstValueFrom(this.paymentService.createPayment(paymentRequest));

      if (!paymentResponse || !paymentResponse.ok) {
        throw new Error(paymentResponse?.msg || 'No se pudo crear el pago en el sistema');
      }

      const internalPaymentId = paymentResponse.data.id_pago;
      this.paymentId.set(internalPaymentId);

      // 3. Cargar iframe con la URL de APay
      if (apayResponse.url) {
        this.iframeUrl.set(apayResponse.url);
        // Small delay to ensure element exists if we move it in DOM
        setTimeout(() => {
          if (this.paymentIframe) {
            this.paymentIframe.nativeElement.src = apayResponse.url;
          }
        }, 100);
      }

      // 4. Iniciar polling para verificar el estado del pago
      this.startPaymentPolling(apayResponse.id);

    } catch (error: any) {
      console.error('Error procesando pago:', error);
      this.isLoading.set(false);
      this.paymentStatus.set('failed');
      this.errorMessage.set(error.message || 'Error al procesar el pago. Por favor, intenta nuevamente.');
      this.notificationService.error('Error de Pago', this.errorMessage());
    }
  }

  // Start polling for payment status
  private startPaymentPolling(transactionId: string) {
    this.pollingSubscription = this.paymentService.pollPaymentStatus(
      transactionId,
      (payment: PaymentResponse) => {
        console.log('Payment status update:', payment.estado);
        
        if (payment.estado === 'Exitoso') {
          this.handlePaymentSuccess(payment);
        } else if (payment.estado === 'Fallido') {
          this.handlePaymentFailure(payment);
        } else if (payment.estado === 'Reembolsado') {
           this.handlePaymentFailure(payment); // Tratar reembolso como fallo para efectos de registro
        }
      }
    ).subscribe({
      complete: () => {
        console.log('Polling completed');
      },
      error: (error) => {
        console.error('Error en polling:', error);
        // No fallamos inmediatamente si el polling falla una vez, pero si se detiene:
        // Podríamos dejar que el usuario reintente o contacte soporte.
      }
    });
  }

  /**
   * Registro con paymentId tras pago exitoso (tarjeta o cripto).
   */
  private async registerAfterSuccessfulPayment(
    idPago: number,
    referenciaTransaccion: string | null | undefined
  ): Promise<void> {
    const userData = this.userInfoForm.getRawValue();

    const registerData = {
      name: userData.name,
      email: userData.email,
      phone: userData.phone,
      contrasena: userData.contrasena,
      codigo_referido: userData.referralCode || undefined,
      paymentId: idPago,
      id_plan: this.selectedPlan()?.id
    };

    const registerResponse = await firstValueFrom(this.authService.register(registerData));

    if (registerResponse.ok) {
      if (registerResponse.usuario?.uid) {
        try {
          await firstValueFrom(
            this.paymentService.updatePaymentUser({
              id_usuario: registerResponse.usuario.uid,
              referencia_transaccion: referenciaTransaccion || ''
            })
          );
        } catch (updateError) {
          console.error('Error al asociar pago con usuario:', updateError);
        }
      }

      if (registerResponse.token) {
        this.socketService.conectar(registerResponse.token);
        this.socketService.conectado$.subscribe((conectado) => {
          if (conectado) {
            this.chatService.unirseAConversaciones();
            this.chatService.cargarConversaciones();
          }
        });
      }

      this.isLoading.set(false);
      this.paymentStatus.set('success');
      this.notificationService.success(
        '¡Bienvenido!',
        'Tu cuenta ha sido creada y tu suscripción está activa.'
      );
      setTimeout(() => this.router.navigate(['/']), 3000);
      return;
    }

    const rawMsg = registerResponse.msg || '';
    const msg = rawMsg.toLowerCase();
    const likelyAlreadyRegistered =
      msg.includes('ya está') ||
      msg.includes('ya existe') ||
      msg.includes('registrado') ||
      msg.includes('inicia sesión') ||
      msg.includes('iniciar sesión') ||
      (msg.includes('correo') && msg.includes('utiliza'));

    if (likelyAlreadyRegistered) {
      this.isLoading.set(false);
      this.paymentStatus.set('success');
      this.notificationService.info(
        'Cuenta lista',
        rawMsg ||
          'Si el sistema ya creó tu usuario, inicia sesión con el correo y la contraseña que te enviamos.'
      );
      setTimeout(() => this.router.navigate(['/login']), 3500);
      return;
    }

    throw new Error(rawMsg || 'Error al registrar usuario');
  }

  // Handle payment success
  private async handlePaymentSuccess(payment: PaymentResponse) {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }

    this.iframeUrl.set(null);
    this.paymentStatus.set('processing');

    try {
      await this.registerAfterSuccessfulPayment(
        payment.id_pago,
        payment.referencia_transaccion
      );
    } catch (error: any) {
      console.error('Error en registro post-pago:', error);
      this.isLoading.set(false);
      this.paymentStatus.set('success');
      this.errorMessage.set(
        'Tu pago fue exitoso, pero hubo un error creando tu cuenta. Por favor contacta a soporte con la referencia: ' +
          (payment.referencia_transaccion || String(payment.id_pago))
      );
      this.notificationService.warning('Atención', 'Pago exitoso pero error en registro. Contacta soporte.');
    }
  }

  // Handle payment failure
  private handlePaymentFailure(payment: PaymentResponse) {
    this.isLoading.set(false);
    this.paymentStatus.set('failed');
    this.iframeUrl.set(null);

    // Stop polling
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }

    const errorMsg = payment.iso_response_code
      ? `Pago rechazado (Código: ${payment.iso_response_code})`
      : 'El pago no pudo ser procesado';

    this.errorMessage.set(errorMsg);
    this.notificationService.error('Pago Fallido', errorMsg);
  }

  // Handle APay validation errors
  private handleApayError(errorMsg: string) {
    this.isLoading.set(false);
    this.paymentStatus.set('failed');
    this.iframeUrl.set(null);

    let userMessage = 'Error en los datos de la tarjeta';

    if (errorMsg.includes('Tarjeta Invalida')) {
      userMessage = 'Número de tarjeta inválido';
    } else if (errorMsg.includes('Fecha de expiración')) {
      userMessage = 'Fecha de expiración no válida';
    } else if (errorMsg.includes('CVV')) {
      userMessage = 'CVV no válido';
    } else if (errorMsg.includes('Monto')) {
      userMessage = 'Monto de tarjeta no válido';
    }

    this.errorMessage.set(userMessage);
    this.notificationService.error('Error de Validación', userMessage);
  }

  // Format card number with spaces
  formatCardNumber(event: any) {
    let value = event.target.value.replace(/\s/g, '');
    let formattedValue = value.match(/.{1,4}/g)?.join(' ') || value;
    this.paymentForm.patchValue({ cardNumber: formattedValue }, { emitEvent: false });
  }

  // Format expiry date
  formatExpiryDate(event: any) {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length >= 2) {
      value = value.slice(0, 2) + '/' + value.slice(2, 4);
    }
    this.paymentForm.patchValue({ expiryDate: value }, { emitEvent: false });
  }

  // Toggle password visibility
  togglePasswordVisibility() {
    this.showPassword.update(value => !value);
  }

  // Get card type icon
  getCardTypeIcon(): string {
    const cardNumber = this.paymentForm.get('cardNumber')?.value || '';
    const cardType = this.paymentService.detectCardType(cardNumber);

    if (cardType === 'Visa') return '💳 Visa';
    if (cardType === 'Mastercard') return '💳 Mastercard';
    if (cardType === 'American Express') return '💳 Amex';
    return '💳';
  }
}
